// Monthly Adelaide building market updates.
// Targets "Adelaide building market" / "Adelaide construction news" / freshness signals for Google.
// Add a new entry here at the start of each month. The /market-update page
// always renders the latest entry by publishDate.

export const marketUpdateData = {
  "february-2026": {
    slug: "february-2026",
    month: "February",
    year: "2026",
    metaTitle: "Adelaide Building Market Update February 2026 | BuildPilot",
    metaDescription: "Adelaide home building market update for February 2026. Current build costs, land prices in growth corridors, builder lead times, and what it means for buyers in 2026.",
    publishDate: "2026-02-03",
    dateModified: "2026-02-03",
    summary: "Adelaide enters autumn 2026 with a more balanced building market than at any point since 2021. Volume builders are quoting realistic timeframes, land releases in northern growth corridors are catching up to demand, and material costs have plateaued. Most house and land packages still start around the $650K mark for entry-level four-bedroom homes on a finished block.",
    keyMetrics: [
      { label: "Avg Build Cost / sqm", value: "$1,950 to $2,850", change: "Steady", direction: "neutral" },
      { label: "Outer North Land", value: "$220,000 to $300,000", change: "Steady", direction: "neutral" },
      { label: "Outer South Land", value: "$280,000 to $400,000", change: "+1%", direction: "up" },
      { label: "Build Timeframe", value: "6 to 9 months", change: "Stable", direction: "neutral" }
    ],
    sections: [
      {
        title: "Construction Costs",
        content: "Construction costs have been broadly flat over the past quarter. Volume builders are pricing competitively to fill 2026 build slots, with quality four-bedroom volume homes commonly quoted from $360,000 to $480,000 build only. Custom and architecturally designed homes still range much higher depending on inclusions, site conditions and finishes. Pricing on official cost benchmarks should always be cross-checked with builders directly. (Source: ABS Producer Price Index, RevenueSA)."
      },
      {
        title: "Land Market",
        content: "Northern growth corridors at Riverlea, Angle Vale, Two Wells and Munno Para are releasing new stages with stable pricing. Southern coastal estates around Aldinga and Sellicks Beach are still seeing modest premiums for ocean proximity. Adelaide Hills demand remains steady at Mount Barker, with site costs varying significantly on sloping blocks. Established suburbs continue to see knockdown rebuild activity dominate, since vacant land is rare. House and land packages on finished blocks generally start from around $650,000 once construction, land, finishes and statutory costs are added together."
      },
      {
        title: "Builder Capacity",
        content: "Most volume and project builders can commence construction within 2 to 4 months of contract signing where finance and titles are ready. Subcontractor availability has improved compared to the post-pandemic peak. We are not currently seeing any unusual stress signals across the partnered builder network. Buyers should still verify a builder's Building Indemnity Insurance, BLD licence and current project pipeline before signing. (Source: Government of South Australia Consumer and Business Services)."
      },
      {
        title: "Market Outlook",
        content: "Heading into the autumn 2026 building season, the market is in a more balanced state. Interest rate stability through late 2025 has supported buyer confidence, particularly among first home buyers using the SA First Home Owner Grant (caps were removed in June 2024). We expect modest cost increases broadly in line with inflation, steady land prices in most growth corridors, and continued strong activity in the volume builder segment. (Source: RevenueSA, ABS)."
      }
    ],
    tipsThisMonth: [
      "Autumn is a strong time to lock in 2026 build slots before the spring rush.",
      "Compare at least three builders on identical inclusions, not headline price alone.",
      "Confirm site cost allowances are realistic for your specific block, not just the standard estate.",
      "Get formal finance pre-approval before paying any tender fee."
    ],
    relatedLinks: [
      { title: "Building Costs Adelaide 2026", href: "/building-costs-adelaide-2026" },
      { title: "Best Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "House and Land Packages Adelaide", href: "/house-and-land-packages-adelaide" },
      { title: "Compare Adelaide Builders", href: "/compare" }
    ]
  },

  "december-2025": {
    slug: "december-2025",
    month: "December",
    year: "2025",
    metaTitle: "Adelaide Building Market Update December 2025 | BuildPilot",
    metaDescription: "Adelaide home building market update for December 2025. Construction costs, land prices, builder capacity, and what it means for your build.",
    publishDate: "2025-12-01",
    dateModified: "2025-12-01",
    summary: "Construction activity remains steady heading into the summer building season. Land prices have stabilised in most areas, though premium locations continue to see modest growth. Builder lead times have improved from 2023 to 2024 peaks, with most builders now offering 6 to 8 month construction timeframes.",
    keyMetrics: [
      { label: "Avg Build Cost / sqm", value: "$2,100 to $2,800", change: "+3%", direction: "up" },
      { label: "Outer North Land", value: "$165,000 to $220,000", change: "Stable", direction: "neutral" },
      { label: "Outer South Land", value: "$180,000 to $280,000", change: "+2%", direction: "up" },
      { label: "Build Timeframe", value: "6 to 9 months", change: "Improved", direction: "down" }
    ],
    sections: [
      {
        title: "Construction Costs",
        content: "Building costs saw moderate increases of approximately 3% over the December quarter, driven primarily by labour costs and select material price increases. The rapid cost escalation seen in 2022 and 2023 has largely subsided, with builders reporting more predictable pricing and making fixed price contracts more reliable. Volume builders continued to offer competitive pricing on quality four-bedroom homes."
      },
      {
        title: "Land Market",
        content: "Land availability improved in key growth corridors. Northern Adelaide estates at Two Wells, Riverlea and Angle Vale released new stages with competitive pricing. Southern suburbs around Seaford and Aldinga maintained steady demand for beach proximate living. Adelaide Hills land at Mount Barker remained popular. Infill opportunities in established suburbs were limited but present for knockdown rebuild projects."
      },
      {
        title: "Builder Capacity",
        content: "Lead times improved significantly from pandemic era peaks. Most volume and project builders could commence construction within 2 to 4 months of contract signing, weather permitting. Competition for quality tradespeople remained, but builders reported more reliable subcontractor availability."
      },
      {
        title: "Market Outlook",
        content: "The Adelaide building market entered 2026 in a more balanced state. Interest rate stability supported buyer confidence. First home buyers continued to drive significant activity, supported by grant programs. Modest cost increases in line with inflation were expected, with stable land prices in most growth areas."
      }
    ],
    tipsThisMonth: [
      "Summer is peak building season in Adelaide. Lock in your builder early to secure preferred start dates.",
      "Review your building contract for any cost escalation clauses before signing.",
      "Consider getting multiple quotes. Builder pricing varies significantly for similar specifications.",
      "If buying land, secure before Easter when land sales typically slow."
    ],
    relatedLinks: [
      { title: "Building Costs Adelaide 2026", href: "/building-costs-adelaide-2026" },
      { title: "Best Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Compare Adelaide Builders", href: "/compare" }
    ]
  }
};

export const getMarketUpdate = (slug) => marketUpdateData[slug] || null;

export const getLatestMarketUpdate = () => {
  const updates = Object.values(marketUpdateData);
  return updates.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))[0];
};

export const getAllMarketUpdates = () => {
  return Object.values(marketUpdateData)
    .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
};
